<template>
  <section :aria-label="compoundScopeText" class="scopeWrapper column">
    <div class="scopeTitle">{{ compoundScopeText }}</div>

    <div class="scopeStage">
      <div class="scopeStack">
        <img class="scopeLayer" :src="baseSlice" :alt="compoundScopeText" />
        <img v-if="brightSlice" class="scopeLayer" :src="brightSlice" alt="" />
        <img class="scopeLayer" :src="magFocusSlice" alt="" />
        <img class="scopeLayer" :src="spaceSlice" alt="" />
      </div>
    </div>

    <div class="scopeControls">
      <div class="controlRow">
        <label class="controlLabel" for="eyePieceSlider">{{ eyePieceText }}</label>
        <span class="sliderWrap">
          <span class="sliderTrack" aria-hidden="true"></span>
          <input id="eyePieceSlider" class="slider" type="range" min="-1" max="1" step="0.01"
            :value="scope.eyePiecePos" @input="setEyePiece($event.target.value)"
            @change="logChange(eyePieceText, scope.eyePiecePos)" />
        </span>
        <span class="controlValue" aria-hidden="true"></span>
      </div>
      <div class="controlRow">
        <label class="controlLabel" for="magnificationSlider">{{ magnificationText }}</label>
        <span class="sliderWrap">
          <span class="sliderTrack sliderTrack--stops" aria-hidden="true"></span>
          <input id="magnificationSlider" class="slider" type="range" min="0" :max="MAGNIFICATIONS.length - 1" step="1"
            :value="scope.magnificationIndex" @input="setMagnification($event.target.value)"
            @change="logChange(magnificationText, magnificationLabel)" />
        </span>
        <span class="controlValue">{{ magnificationLabel }}</span>
      </div>
      <div class="controlRow">
        <label class="controlLabel" for="focusSlider">{{ focusText }}</label>
        <span class="sliderWrap">
          <span class="sliderTrack" aria-hidden="true"></span>
          <input id="focusSlider" class="slider" type="range" min="-1" max="1" step="0.01" :value="scope.focusPos"
            @input="setFocus($event.target.value)" @change="logChange(focusText, scope.focusPos)" />
        </span>
        <span class="controlValue" aria-hidden="true"></span>
      </div>
      <div class="controlRow">
        <label class="controlLabel" for="brightnessSlider">{{ brightnessText }}</label>
        <span class="sliderWrap">
          <span class="sliderTrack" aria-hidden="true"></span>
          <input id="brightnessSlider" class="slider" type="range" min="0" max="1" step="0.01"
            :value="scope.brightnessPos" @input="setBrightness($event.target.value)"
            @change="logChange(brightnessText, scope.brightnessPos)" />
        </span>
        <span class="controlValue" aria-hidden="true"></span>
      </div>
    </div>

    <div class="scopeFooter">
      <button class="btn btn-primary ripple" @click="done" tabindex="0">{{ doneText }}</button>
    </div>
  </section>
</template>
<script>
import Constants from '../enums/Constants';
import BiologyLabEnum from '../enums/BiologyLabEnum';
import { entry } from '../services/xapi';
import xapiEntryTypes from '../enums/xapiEntryTypes';
import {
  scope,
  MAGNIFICATIONS,
  magnification,
  spaceSliceIndex,
  magSliceNumber,
  focusSliceIndex,
  brightSliceIndex,
} from '../services/CompoundScope';

const slices = require.context('../assets/CompoundScopeGraphicSlices', false, /\.png$/);
const slice = (name) => slices('./' + name + '.png');
const pad = (value, length) => String(value).padStart(length, '0');

export default {
  name: 'CompoundScopeView',
  emits: ['done'],
  data: function () {
    return {
      scope,
      MAGNIFICATIONS,
      baseSlice: slice('CompScope_BASE'),
      compoundScopeText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.COMPOUND_SCOPE),
      eyePieceText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.EYE_PIECE),
      magnificationText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.MAGNIFICATION),
      focusText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.FOCUS),
      brightnessText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.BRIGHTNESS),
      doneText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.DONE),
    };
  },
  mounted() {
    // The slider swaps the layer graphics as it moves, so make sure every slice
    // is in the browser cache before the student touches anything.
    slices.keys().forEach((key) => {
      const image = new Image();
      image.src = slices(key);
    });
  },
  methods: {
    setEyePiece: function (value) {
      scope.eyePiecePos = parseFloat(value);
    },
    setMagnification: function (value) {
      scope.magnificationIndex = parseInt(value, 10);
    },
    setFocus: function (value) {
      scope.focusPos = parseFloat(value);
    },
    setBrightness: function (value) {
      scope.brightnessPos = parseFloat(value);
    },
    logChange: function (name, value) {
      entry(xapiEntryTypes.STATE, {
        extensions: {
          name: Constants.MICROSCOPE,
          control: name,
          state: typeof value === 'number' ? value.toFixed(2) : value,
        },
      });
    },
    done: function () {
      this.$emit('done');
    },
  },
  computed: {
    magnificationLabel: function () {
      return 'x' + magnification();
    },
    spaceSlice: function () {
      return slice('CompScope_Space_' + pad(spaceSliceIndex(), 2));
    },
    magFocusSlice: function () {
      return slice('CompScope_Mag_' + magSliceNumber() + '_Focus_' + focusSliceIndex());
    },
    brightSlice: function () {
      const index = brightSliceIndex();
      return index ? slice('CompScope_Bright_' + pad(index, 2)) : null;
    },
  },
};
</script>
<style lang="scss" scoped src="../styles/CompoundScopeView.scss"></style>
